// Small DOM helpers. The Manager is plain TypeScript over Calcite custom elements
// (SPEC 8.1: no framework), so these stand in for one.

/** Marks a string as already-safe HTML inside an `html` template. */
export class Raw {
  constructor(readonly value: string) {}
}
export const raw = (s: string): Raw => new Raw(s);

export function escapeHtml(v: unknown): string {
  return String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Tagged template that escapes every interpolation unless it is `raw(...)` or an
 * array of such. Layer names, usernames and error text all come from the Portal;
 * none of it may become markup.
 */
export function html(strings: TemplateStringsArray, ...values: unknown[]): string {
  let out = strings[0];
  for (let i = 0; i < values.length; i++) {
    out += render(values[i]) + strings[i + 1];
  }
  return out;
}

function render(v: unknown): string {
  if (v instanceof Raw) return v.value;
  if (Array.isArray(v)) return v.map(render).join("");
  if (v === null || v === undefined || v === false) return "";
  return escapeHtml(v);
}

export function $<T extends Element = HTMLElement>(root: ParentNode, selector: string): T {
  const found = root.querySelector<T>(selector);
  if (!found) throw new Error(`Expected an element matching ${selector}`);
  return found;
}

export function $$<T extends Element = HTMLElement>(root: ParentNode, selector: string): T[] {
  return [...root.querySelectorAll<T>(selector)];
}

/** `on(root, "calciteButtonClick", "[data-act=save]", handler)` — one listener, delegated. */
export function on<E extends Event = Event>(
  root: ParentNode & EventTarget,
  type: string,
  selector: string,
  handler: (event: E, element: HTMLElement) => void
): void {
  root.addEventListener(type, (event) => {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(selector);
    if (target && root.contains(target)) handler(event as E, target);
  });
}

export const fmtDate = (epochMs: number | null | undefined): string =>
  epochMs ? new Date(epochMs).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

export const fmtAgo = (epochMs: number | null | undefined): string => {
  if (!epochMs) return "never";
  const s = Math.max(0, Math.round((Date.now() - epochMs) / 1000));
  if (s < 90) return `${s}s ago`;
  if (s < 5400) return `${Math.round(s / 60)} min ago`;
  if (s < 172800) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
};

export const errorText = (e: unknown): string => {
  const message = e instanceof Error ? e.message : String(e);
  const details = (e as { details?: string[] })?.details;
  return details?.length ? `${message} (${details.join("; ")})` : message;
};
